import { useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { usePublicConfig, waLink, viberLink } from "@/lib/config";

const ServiceButtons = ({ numbers, text, testPrefix, compact }) => (
  <>
    {numbers.whatsapp && (
      <a
        href={waLink(numbers.whatsapp, text)}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`${testPrefix}-whatsapp`}
        className={`flex items-center justify-center gap-2 border border-navy/15 bg-white font-semibold uppercase tracking-[0.14em] text-navy transition-colors hover:border-gold hover:text-gold-dark ${compact ? "px-4 py-2.5 text-[10px]" : "px-5 py-3 text-[11px]"}`}
      >
        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: "#25D366" }} />
        WhatsApp
      </a>
    )}
    {numbers.viber && (
      <a
        href={viberLink(numbers.viber)}
        data-testid={`${testPrefix}-viber`}
        className={`flex items-center justify-center gap-2 border border-navy/15 bg-white font-semibold uppercase tracking-[0.14em] text-navy transition-colors hover:border-gold hover:text-gold-dark ${compact ? "px-4 py-2.5 text-[10px]" : "px-5 py-3 text-[11px]"}`}
      >
        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: "#7360F2" }} />
        Viber
      </a>
    )}
  </>
);

/** Inline buttons for the product page — prefilled with the product name. */
export const ChatOrderButtons = ({ productName }) => {
  const { t } = useLanguage();
  const cfg = usePublicConfig();
  if (!cfg || (!cfg.whatsapp && !cfg.viber)) return null;
  const text = (t.chat?.prefillProduct || "Hello! I'd like to order: {product}").replace("{product}", productName);
  return (
    <div data-testid="product-chat-order" className="mt-4">
      <p className="label-luxe mb-2 text-navy/50">{t.chat?.order || "Order via chat"}</p>
      <div className="flex flex-wrap gap-3">
        <ServiceButtons numbers={cfg} text={text} testPrefix="product-order" />
      </div>
    </div>
  );
};

/** Floating contact button on every public page (hidden until a number is configured). */
const ChatOrderFab = () => {
  const { t } = useLanguage();
  const { pathname } = useLocation();
  const cfg = usePublicConfig();
  const [open, setOpen] = useState(false);
  if (!cfg || (!cfg.whatsapp && !cfg.viber) || pathname.startsWith("/admin")) return null;
  const numbers = { whatsapp: cfg.whatsapp, viber: cfg.viber };
  const text = t.chat?.prefill || "Hello! I'd like to place an order.";
  return (
    <div data-testid="chat-order-fab" className="fixed bottom-6 end-6 z-40 flex flex-col items-end gap-2">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.95 }}
            transition={{ duration: 0.18 }}
            className="flex flex-col gap-2 border border-navy/10 bg-white/95 p-3 shadow-xl backdrop-blur"
          >
            <p className="max-w-60 px-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-navy/50">
              {t.chat?.help || t.chat?.order || "Need help? Contact us"}
            </p>
            <ServiceButtons numbers={numbers} text={text} testPrefix="chat-order" compact />
          </motion.div>
        )}
      </AnimatePresence>
      <button
        type="button"
        aria-label={t.chat?.contactUs || t.chat?.order || "Contact us"}
        data-testid="chat-order-toggle"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 rounded-full bg-navy px-4 py-3 text-gold shadow-lg transition-transform hover:scale-105"
      >
        {open ? <X size={18} /> : <MessageCircle size={20} />}
        {!open && (
          <span className="hidden text-[10px] font-semibold uppercase tracking-[0.15em] sm:inline">
            {t.chat?.contactUs || "Contact us"}
          </span>
        )}
      </button>
    </div>
  );
};

export default ChatOrderFab;
